import { get as _get, set as _set } from 'lodash'
import {
  ConfigurationDetail,
  DatadogEncodingRun,
  EncodingCounters,
  EncodingError,
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  IdentifierType,
  ItemConfigurations,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrderRowDetail,
  Products,
  TmrTag,
  WorkOrder,
  WorkOrderRow,
  WorkOrderRows
} from 'stylewhere/api'
import { __, T } from 'stylewhere/i18n'
import {
  AppStore,
  FormSchema,
  FormSchemaData,
  getDataFromSchema,
  jsonToFormSchema,
  OperationConfig,
  RfidReader,
  Router
} from 'stylewhere/shared'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  CUSTOM_COMMANDS_OPERATION_TYPE,
  compareSizeLabels,
  enabledEanAndAddon,
  enabledWamGroupedProduct,
  getEncodingBlockingErrors,
  getEncodingTagPassword,
  getInitialType,
  isEncodingErrorCheckingByRole,
  isExpectOnlyMandatoryIdentifiers
} from 'stylewhere/shared/utils'
import {
  isMutualAuth2StepEnabled,
  isNfcAuthEnabled,
  NfcAuthOperationOptions,
  readNfcAuthenticatedTag
} from 'stylewhere/shared/nfcAuthentication'
import { Extensions } from './Extensions'

export type OverrideProductInfoFieldsContext = 'encodingForm'

export interface GetProductionOrderDetails {
  productionOrderRow?: ProductionOrderRow
  workOrderRow?: WorkOrderRow
  encodingValidationResponse?: EncodingValidationResponse
}

export class EncodingExtensions extends Extensions {
  static async beforeConfirm<O extends any[]>(
    operation: OperationConfig,
    data: FormSchemaData,
    // Possono essere una lista di items, di parcels o altro a seconda del tipo di operazione
    objects: O
  ): Promise<boolean> {
    return true
  }

  static formSchema(operation: EncodingOperationConfig, additionalData?: any, filterKey?: string[]): FormSchema {
    if (!operation) return []
    const initialType = getInitialType(operation)
    const rawFormSchema = operation.formSchema as any
    const hasFormSchema = Array.isArray(rawFormSchema)
      ? rawFormSchema.length > 0
      : !!rawFormSchema && Object.keys(rawFormSchema).length > 0
    if (initialType === 'formSchema' && hasFormSchema) return jsonToFormSchema(rawFormSchema)

    if (operation.templatePath && operation.templatePath === 'encodingOutboundSap') {
      return []
    }

    if (initialType === 'product') {
      return [
        {
          type: 'text',
          name: 'product.code',
          label: __(T.misc.upc),
          required: true,
          autoFocus: true,
          submitOnEnter: true
        }
      ]
    }

    if (initialType === 'wam') {
      return [
        {
          type: 'text',
          name: 'wam',
          label: __(T.custom.WAM),
          required: true,
          autoFocus: true,
          submitOnEnter: true
        }
      ]
    }

    if (initialType === 'order') return this.getOrderFormSchema(operation, additionalData, true)

    if (initialType === 'orderRow') return this.getOrderFormSchema(operation, additionalData, false)

    if (initialType === 'workOrder') {
      return [
        {
          type: 'text',
          name: 'workOrder',
          initialType: 'workOrder',
          label: __(T.misc.work_order),
          required: true,
          autoFocus: true,
          submitOnEnter: true
        }
      ]
    }

    return []
  }

  static getOrderFormSchema(
    operation: EncodingOperationConfig,
    additionalData: any = {},
    showProductionOrder: boolean = false
  ): FormSchema {
    const mode = operation.options?.productionOrderRowSelectionMode
      ? operation.options.productionOrderRowSelectionMode
      : 'byOrderAndRowCode'

    const formSchema: FormSchema = []
    let productionOrderRowLabel = __(T.misc.production_order_sku)
    if (enabledEanAndAddon(operation)) {
      productionOrderRowLabel = 'EAN + Addon'
    } else if (mode === 'byOrderAndRowCode') {
      productionOrderRowLabel = __(T.misc.production_order_row)
    }

    if (showProductionOrder)
      formSchema.push({
        type: 'text',
        name: 'productionOrder',
        label: __(T.misc.production_order),
        required: !additionalData.productionOrder,
        autoFocus: !additionalData.productionOrder,
        hide: !!additionalData.productionOrder,
        submitOnEnter: true
      })

    formSchema.push({
      type: 'text',
      name: 'productionOrderRow',
      label: productionOrderRowLabel,
      required: !!additionalData.productionOrder || !showProductionOrder,
      autoFocus: !!additionalData.productionOrder || !showProductionOrder,
      hide: !additionalData.productionOrder && showProductionOrder,
      submitOnEnter: true
    })

    return formSchema
  }

  static async getProductId(productCode, operationId, runId?: string) {
    const encodingProductsResult = await Encodings.findProduct({ code: productCode, operationId }, runId)
    if (encodingProductsResult.ok) {
      return encodingProductsResult.data?.id
    } else if (encodingProductsResult.status !== 400) {
      const products = await Products.searchCodes([productCode])
      if (products.content?.[0]?.id) {
        return products.content[0].id
      }
    }

    throw new Error(__(T.error.product_not_found))
  }

  static async getWorkOrderRowId(workOrderRowCode, operationId) {
    const encodingProductsResult = await WorkOrderRows.search<WorkOrderRow>({
      equalCodes: workOrderRowCode,
      operationId
    })
    if (encodingProductsResult.content.length > 0) {
      return encodingProductsResult.content[0].id
    }
    throw new Error(__(T.error.couldn_find_work_order))
  }

  static getDataDogFormSchemaLabel = (
    operation: EncodingOperationConfig,
    data?: FormSchemaData,
    initialType?: string
  ) => {
    const safeData = data ?? {}
    let label = ''
    if (initialType === 'identifier' || initialType === 'identifierOrderRow') {
      label = _get(safeData, 'identifier') ?? ''
    } else if (initialType === 'formSchema') {
      const schema = EncodingExtensions.formSchema(operation, safeData)
      let find = schema.find((el) => el.isProductCode || el.initialType === 'product')
      if (find) {
        label = _get(safeData, find.name) ?? ''
      } else {
        find = schema.find((el) => ['order', 'orderRow'].includes(el.initialType))
        if (find) {
          label = safeData.productionOrderRow?.code
        }
      }
    } else if (initialType === 'product') {
      label = safeData.product?.code
    } else if (initialType === 'order' || initialType === 'orderRow') {
      label = safeData.productionOrderRow?.code
    } else if (initialType === 'wam') {
      label = safeData.wam
    }
    return label
  }

  static getItemTypeConfiguration = async (operation: EncodingOperationConfig) => {
    let configuration = undefined
    let itemConfig = ''
    let itemConfigDefault = ''
    const keys = Object.keys(operation.options)
    let key
    for (const element of keys) {
      if (element.includes('itemTypeConfig_')) {
        key = element.replace('itemTypeConfig_', '')
        if (key === 'default') {
          itemConfigDefault = operation.options[element] || ''
        } else if (itemConfig === '') {
          itemConfig = operation.options[element] || ''
        }
      }
    }
    if (itemConfig !== '' || itemConfigDefault !== '') {
      const res: any = await ItemConfigurations.search({
        equalCodes: itemConfig === '' ? itemConfigDefault : itemConfig
      })
      if (res?.content && res.content.length > 0 && res.content[0].details && res.content[0].details.length > 0) {
        configuration = res.content[0].details
      }
    }
    return configuration
  }

  /**
   * Returns the item attributes from the form schema if the operation initial type is formSchema.
   */
  static getFormSchemaItemAttributes = (
    operation: EncodingOperationConfig,
    data: FormSchemaData
  ): Record<string, unknown> => {
    if (getInitialType(operation) !== 'formSchema') return {}

    const schema = EncodingExtensions.formSchema(operation, data)
    const formattedData = getDataFromSchema(data, schema)
    const itemAttributes: Record<string, unknown> = {}

    schema.forEach((field) => {
      if (
        field.isProductCode ||
        ['product', 'order', 'orderRow'].includes(field.initialType ?? '') ||
        ['product', 'order', 'orderRow'].includes(field.referenceInitialType ?? '')
      ) {
        return
      }
      const value = _get(formattedData, field.name)
      if (value !== undefined && value !== null && value !== '') {
        const key = field.name.startsWith('attributes.') ? field.name.slice('attributes.'.length) : field.name
        _set(itemAttributes, key, value)
      }
    })

    return itemAttributes
  }

  static getBlockingErrorMessage(
    _operation: EncodingOperationConfig,
    error: EncodingError,
    res: EncodingValidationResponse
  ): string {
    return __(
      T.error[error.errorCode],
      error.errorCode === 'MISSING_EAN_ATTRIBUTE' ? { productCode: res.item?.product?.code ?? ' ' } : {}
    )
  }

  static async getItemConfiguration(
    operation: EncodingOperationConfig,
    data: FormSchemaData,
    operationAttributes?: any,
    runId?: string
  ) {
    try {
      const initialType = getInitialType(operation)

      let extraOperationAttributes = {}
      let itemAttributes = undefined
      if (initialType === 'formSchema') {
        extraOperationAttributes = EncodingExtensions.getFormSchemaItemAttributes(operation, data)
        itemAttributes = EncodingExtensions.getFormSchemaItemAttributes(operation, data)
      }

      const encodingValidation: EncodingValidationRequest = {
        identifiers: [],
        operationId: operation.id,
        placeId: AppStore.defaultWorkstation?.placeId,
        workstationId: AppStore.defaultWorkstation?.id,
        operationAttributes: {
          ...operationAttributes,
          ...extraOperationAttributes
        },
        itemAttributes
      }
      let getCounters = false
      if (initialType === 'formSchema') {
        const schema = EncodingExtensions.formSchema(operation, data)
        let find = schema.find((el) => el.isProductCode || el.initialType === 'product')
        if (find) {
          encodingValidation.startEntityId = await this.getProductId(_get(data, find.name), operation.id, runId)
        } else {
          find = schema.find((el) => ['order', 'orderRow'].includes(el.initialType))
          if (find) {
            getCounters = true
            encodingValidation.startEntityId = data.productionOrderRow.id
          } else {
            throw new Error(__(T.error.no_item_configuration))
          }
        }
      } else if (initialType === 'product') {
        encodingValidation.startEntityId = await this.getProductId(data.product.code, operation.id, runId)
      } else if (initialType === 'workOrder') {
        encodingValidation.startEntityId = await this.getWorkOrderRowId(data.workOrderRow, operation.id)
      } else if (initialType === 'order' || initialType === 'orderRow') {
        encodingValidation.startEntityId = data.productionOrderRow.id
      } else if (initialType === 'wam') {
        encodingValidation.startCode = data.wam
      }
      const res = await Encodings.validate(encodingValidation, runId)
      if (res) {
        let configurationDetails
        if (res.configurationDetails) configurationDetails = res.configurationDetails
        else if (res.configuration?.details) configurationDetails = res.configuration.details
        if (!configurationDetails) configurationDetails = []

        if (configurationDetails.length === 0) {
          const missingOptionError = res.errors.find((e) => e.errorCode === 'Missing Operation Option')
          if (missingOptionError) {
            throw new Error(__(T.error.missing_operation_option, { error: missingOptionError.ref }))
          }
          throw new Error(__(T.error.no_item_configuration))
        }

        const blockingErrors = res.errors.find((e) =>
          getEncodingBlockingErrors(operation.options.enableUhfWrite).includes(e.errorCode)
        )
        if (blockingErrors) {
          throw new Error(this.getBlockingErrorMessage(operation, blockingErrors, res))
        }

        let counters: EncodingCounters | undefined
        if (initialType === 'workOrder' && encodingValidation.startEntityId) {
          counters = (await Encodings.getWorkOrderRowCounter(encodingValidation.startEntityId)).data
        } else if ((['order', 'orderRow'].includes(initialType) || getCounters) && !!encodingValidation.startEntityId) {
          counters = (await Encodings.getProductionOrderRowCounter(encodingValidation.startEntityId)).data
        }

        const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(operation)
        configurationDetails.forEach((configDetail) => {
          if (!expectOnlyMandatoryIdentifiers || (expectOnlyMandatoryIdentifiers && !configDetail.optional)) {
            encodingValidation.identifiers.push({ ...configDetail })
          }
        })
        return { encodingValidationResponse: res, encodingValidation, counters }
      } else {
        throw new Error(__(T.error.no_item_configuration))
      }
    } catch (error) {
      throw new Error((error as any)?.message ?? __(T.error.no_item_configuration))
    }
  }

  // set identifiers status with association by group
  static async set_identifiers_status(
    encodingValidation: EncodingValidationRequest,
    result?: any,
    enabledUhfWrite?: boolean,
    expectOnlyMandatoryIdentifiers?: boolean
  ) {
    encodingValidation.identifiers
      .filter((idf) => !!idf.code)
      .forEach((idf) => {
        idf._status = 'CONFIRMED'
        idf._error = undefined
      })
    if (result?.item?.identifiers) {
      encodingValidation.identifiers
        // check for enabledUhfWrite can be removed when readingRequired flag is set correctly for all environments
        .filter((f) =>
          enabledUhfWrite ? f.identifierType === 'UHF_TAG' : (f.code && f.code !== '') || f._possibleCodes?.length > 0
        )
        .forEach((idf) => {
          const roles = ['UHF', 'UHF_TAG'].includes(idf.role) ? ['UHF', 'UHF_TAG'] : [idf.role]
          EncodingExtensions.promotePossibleCode(idf, result, encodingValidation)
          EncodingExtensions.resolveGroupIdentifier(idf, roles, result, encodingValidation)
        })
    }

    if (result?.item?.attributes?.ignoredTags && result.item.attributes.ignoredTags !== '') {
      const ignoredTags = result.item.attributes.ignoredTags.split(',')
      if (ignoredTags.length > 0) {
        ignoredTags.forEach((tag) => {
          const index = encodingValidation.identifiers.findIndex((idf) => idf.code === tag.trim())
          if (index >= 0) {
            encodingValidation.identifiers[index]._status = 'IGNORED'
          }
        })
      }
    }
    if (result?.errors) {
      result.errors.forEach((error) => {
        switch (error.errorCode) {
          case 'Tag Mismatch':
            EncodingExtensions.handleTagMismatchError(error, encodingValidation)
            break
          case 'EXTRA_TAG':
            EncodingExtensions.handleExtraTagError(error, encodingValidation, result, expectOnlyMandatoryIdentifiers)
            break
          default:
            EncodingExtensions.handleDefaultError(error, encodingValidation, result, expectOnlyMandatoryIdentifiers)
        }
      })
    }
    return encodingValidation
  }

  private static removePossibleCodeFromOthers(encodingValidation: EncodingValidationRequest, promotedCode: string) {
    encodingValidation.identifiers.forEach((idft) => {
      if (!idft.code && idft._possibleCodes?.some((codeObj) => codeObj.code === promotedCode)) {
        idft._possibleCodes = idft._possibleCodes.filter((codeObj) => codeObj.code !== promotedCode)
        if (idft._possibleCodes?.length === 0 && idft._status === 'PROCESSING') {
          idft._status = 'TO_BE_READ'
        }
      }
    })
  }

  private static promotePossibleCode(idf: any, result: any, encodingValidation: EncodingValidationRequest) {
    if (!idf._possibleCodes?.length) return
    const findRoleIdentifier = result.item.identifiers.find(
      (idt) => idf._possibleCodes?.some((codeObj) => codeObj.code === idt.code) && idt.role === idf.role
    )
    if (!findRoleIdentifier) return
    idf.code = findRoleIdentifier.code
    idf.tid = findRoleIdentifier.tid || ''
    idf._possibleCodes = undefined
    idf._status = 'CONFIRMED'
    idf._read = true
    idf._error = undefined
    EncodingExtensions.removePossibleCodeFromOthers(encodingValidation, findRoleIdentifier.code)
  }

  private static resolveGroupIdentifier(
    idf: any,
    roles: string[],
    result: any,
    encodingValidation: EncodingValidationRequest
  ) {
    const find = result.item.identifiers.find(
      (idt) => idt.code === idf.code && roles.includes(idt.role) && idt.type === idf.identifierType
    )
    if (!find?.group) return

    let group = result.item.identifiers.find(
      (idt) => idt.group === find.group && idt.code !== idf.code && idt.type !== find.type
    )
    if (!group) {
      group = result.item.identifiers.find(
        (idt) => idt.group === find.group && idt.code !== idf.code && idt.type === find.type && idt.role !== find.role
      )
    }
    if (!group) return

    const findConfiguration = encodingValidation.identifiers.find(
      (f) => f.identifierType === group.type && f.role === group.role
    )
    if (findConfiguration?.readingRequired) return

    const exist = encodingValidation.identifiers.filter(
      (idft) =>
        idft.identifierType === group.type &&
        idft.code !== group.code &&
        idft.role === group.role &&
        idft._read &&
        idft._status !== 'ERROR'
    )
    if (exist.length > 0) {
      encodingValidation.identifiers.push({
        identifierType: group.type,
        role: exist[0].role ?? '',
        code: exist[0].code ?? '',
        _read: true,
        _status: 'ERROR',
        _error: 'Extra tag'
      })
    }
    encodingValidation.identifiers
      .filter((idft) => idft.identifierType === group.type && idft._status !== 'ERROR')
      .forEach((idft) => {
        idft._status = 'CONFIRMED'
        idft._error = undefined
        idft.code = group.code
      })
  }

  static activeReplacedAssocitionForSimpleItemIdentifier() {
    return false
  }

  static forceSimpleItemIdentifierAsOptional() {
    return false
  }

  static getStatusGroupedIdentifier(group: any): 'PROCESSING' | 'EXPECTED' | 'ERROR' {
    return group.type === 'SIMPLE_ITEM_IDENTIFIER'
      ? this.activeReplacedAssocitionForSimpleItemIdentifier() && group.status === 'REPLACED_ASSOCIATION'
        ? 'ERROR'
        : 'PROCESSING'
      : 'EXPECTED'
  }

  private static applyGroupedIdentifier(
    group: any,
    encodingValidation: EncodingValidationRequest,
    configurationDetails: ConfigurationDetail[],
    res: any
  ) {
    const existing = encodingValidation.identifiers.find(
      (c) => c.identifierType === group.type && c.role === group.role
    )
    if (existing?.code) return
    const findConfiguration = configurationDetails.find((f) => f.identifierType === group.type && f.role === group.role)
    if (findConfiguration?.readingRequired) return
    if (existing) {
      existing.identifierId = group.id
      existing.code = group.code ?? ''
      existing.status = group.status ?? ''
      existing._status = this.getStatusGroupedIdentifier(group)
      existing._read = false
      existing._error = undefined
      existing.group = group.group ?? ''
      existing.itemId = res.item.id ?? ''
      existing.logicKillable = EncodingExtensions.check_tag_is_logic_killable(group)
    } else {
      encodingValidation.identifiers.push({
        identifierId: group.id,
        identifierType: group.type,
        role: group.role ?? '',
        code: group.code ?? '',
        status: group.status ?? '',
        group: group.group ?? '',
        itemId: res.item.id ?? '',
        _read: false,
        _status: this.getStatusGroupedIdentifier(group),
        _error: undefined,
        logicKillable: EncodingExtensions.check_tag_is_logic_killable(group)
      })
    }
  }

  private static handleTagMismatchError(error: any, encodingValidation: EncodingValidationRequest) {
    const errorLabel = T.identifier_error[error.errorCode] ? __(T.identifier_error[error.errorCode]) : error.errorCode
    encodingValidation.identifiers.forEach((idf) => {
      idf._status = 'ERROR'
      idf._error = errorLabel
    })
  }

  private static handleExtraTagError(
    error: any,
    encodingValidation: EncodingValidationRequest,
    result: any,
    expectOnlyMandatoryIdentifiers?: boolean
  ) {
    if (this.shouldSkipOptionalIdentifierError(error, result, expectOnlyMandatoryIdentifiers)) {
      return
    }
    const errorLabel = T.identifier_error[error.errorCode] ? __(T.identifier_error[error.errorCode]) : error.errorCode
    const referenceIdentifier = encodingValidation.identifiers.find(
      (idf) => idf.code === error.ref || idf.tid === error.ref
    )
    if (referenceIdentifier) {
      // referenceIdentifier è il tag fisico letto che il backend segnala come extra: va sempre
      // marcato come errore, mostrando il role indicato dal backend (anche se duplica quello di
      // un altro tag già confermato: rappresenta comunque il tipo reale di questo tag)
      referenceIdentifier._status = 'ERROR'
      referenceIdentifier._error = errorLabel
      referenceIdentifier._errorCode = error.errorCode
      if (error.payload?.role) {
        referenceIdentifier.role = error.payload.role
      }

      // bad case: backend set the extra tag as one of the already read ones
      result.item.identifiers.forEach((idt) => {
        if (this.isOptionalRole(idt.role, result, expectOnlyMandatoryIdentifiers)) {
          return
        }
        let identifierToReplace = encodingValidation.identifiers.find(
          (idf) => !idf.code && idf.identifierType === idt.type && idf.role === idt.role
        )
        if (!identifierToReplace) {
          identifierToReplace = encodingValidation.identifiers.find(
            (idf) => idf.code === idt.code && idf.identifierType === idt.type
          )
          // se l'identifier locale ha già questo esatto code, il backend lo ha già confermato
          // correttamente in item.identifiers: non va marcato come EXTRA_TAG (vedi GUCCI RTW bug)
          if (identifierToReplace?.code === idt.code) {
            return
          }
        }
        if (identifierToReplace) {
          identifierToReplace.code = idt.code
          identifierToReplace._status = 'ERROR'
          identifierToReplace._error = errorLabel
          identifierToReplace._errorCode = error.errorCode
        } else if (
          idt.code !== referenceIdentifier.code &&
          !encodingValidation.identifiers.some((idf) => idf.code === idt.code)
        ) {
          encodingValidation.identifiers.push({
            code: idt.code,
            identifierType: idt.type,
            role: idt.role,
            _read: true,
            _status: 'ERROR',
            _error: errorLabel,
            _errorCode: error.errorCode
          })
        }
      })
    } else {
      // happy case: backend set the extra tag as the one we've just read
      encodingValidation.identifiers.push({
        code: error.ref,
        identifierType: error.payload?.identifierType || 'UHF_TAG',
        role: error.payload?.role || '',
        _read: true,
        _status: 'ERROR',
        _error: errorLabel,
        _errorCode: error.errorCode
      })
    }
  }

  private static handleDefaultError(
    error: any,
    encodingValidation: EncodingValidationRequest,
    result: any,
    expectOnlyMandatoryIdentifiers?: boolean
  ) {
    if (this.shouldSkipOptionalIdentifierError(error, result, expectOnlyMandatoryIdentifiers)) {
      return
    }
    const errorLabel = T.identifier_error[error.errorCode] ? __(T.identifier_error[error.errorCode]) : error.errorCode
    let index = encodingValidation.identifiers.findIndex((idf) => idf.code === error.ref || idf.tid === error.ref)
    if (isEncodingErrorCheckingByRole() && index === -1) {
      index = encodingValidation.identifiers.findIndex((idf) => idf.role === error.ref)
    }
    if (index >= 0) {
      if (!encodingValidation.identifiers[index].role) {
        encodingValidation.identifiers[index].role = error.payload?.role ?? error.payload?.identifierType
      }

      encodingValidation.identifiers[index]._status = ['MISSING_MANDATORY_TAG', 'MISSING_MANDATORY_TAG_DUAL'].includes(
        error.errorCode
      )
        ? 'EXPECTED'
        : 'ERROR'
      encodingValidation.identifiers[index]._error = this.appendUniqueMessage(
        encodingValidation.identifiers[index]._error,
        errorLabel
      )
      if (error.payload?.errorMessage) {
        encodingValidation.identifiers[index]._errorMessage = this.appendUniqueMessage(
          encodingValidation.identifiers[index]._errorMessage,
          error.payload.errorMessage
        )
      }
    } else if (
      !['MISSING_MANDATORY_TAG', 'MISSING_MANDATORY_TAG_DUAL', 'MISSING_OPTIONAL_TAG'].includes(error.errorCode) &&
      error.payload?.identifierType &&
      !result.item.identifiers.some((idt) => idt.code === error.ref && idt.type === error.payload?.identifierType)
    ) {
      encodingValidation.identifiers.push({
        code: error.ref,
        identifierType: error.payload?.identifierType || 'UHF_TAG',
        role: error.payload?.role || error.payload?.identifierType,
        _read: true,
        _status: 'ERROR',
        _error: errorLabel,
        _errorCode: error.errorCode
      })
      encodingValidation.identifiers.forEach((idft) => {
        if (!idft.code && idft._possibleCodes?.some((codeObj) => codeObj.code === error.ref)) {
          idft._possibleCodes = idft._possibleCodes.filter((codeObj) => codeObj.code !== error.ref)
        }
      })
    }
  }

  private static shouldSkipOptionalIdentifierError(error: any, result: any, expectOnlyMandatoryIdentifiers?: boolean) {
    if (!expectOnlyMandatoryIdentifiers) {
      return false
    }

    const optionalRoles = new Set(
      (result?.configuration?.details || []).filter((detail) => detail.optional).map((detail) => detail.role)
    )
    if (optionalRoles.size === 0) {
      return false
    }

    const payloadRole = error?.payload?.role
    if (payloadRole && this.isOptionalRole(payloadRole, result, expectOnlyMandatoryIdentifiers)) {
      return true
    }

    const referenceIdentifier = result?.item?.identifiers?.find((identifier) => {
      const matchesRef = identifier.code === error?.ref || identifier.tid === error?.ref
      if (!matchesRef) {
        return false
      }
      if (!error?.payload?.identifierType) {
        return true
      }
      return identifier.type === error.payload.identifierType
    })

    return !!(
      referenceIdentifier?.role && this.isOptionalRole(referenceIdentifier.role, result, expectOnlyMandatoryIdentifiers)
    )
  }

  private static isOptionalRole(role?: string, result?: any, expectOnlyMandatoryIdentifiers?: boolean) {
    if (!expectOnlyMandatoryIdentifiers || !role) {
      return false
    }
    return !!result?.configuration?.details?.some((detail) => detail.role === role && detail.optional)
  }

  private static appendUniqueMessage(currentValue?: string, newMessage?: string) {
    if (!newMessage) {
      return currentValue
    }
    if (!currentValue) {
      return newMessage
    }
    const messageAlreadyPresent = currentValue
      .split(',')
      .map((message) => message.trim())
      .includes(newMessage.trim())
    if (messageAlreadyPresent) {
      return currentValue
    }
    return `${currentValue}, ${newMessage}`
  }

  static async validate(
    encodingValidation: EncodingValidationRequest,
    checkConfiguration = true,
    desiredCode = false,
    enabledUhfWrite = false,
    expectOnlyMandatoryIdentifiers = false,
    runId?: string
  ) {
    try {
      const knownRoleIdentifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: desiredCode ? idf.desiredCode : undefined
        }))

      // for unknown role identifiers, only one for each code is included in the request
      const unknownRoleIdentifiers: ConfigurationDetail[] = []
      encodingValidation.identifiers
        .filter((idf) => idf._possibleCodes?.length > 0)
        .forEach((idf) => {
          idf._possibleCodes.forEach((codeObj) => {
            if (!unknownRoleIdentifiers.some((unkRoleIdf) => unkRoleIdf.code === codeObj.code)) {
              unknownRoleIdentifiers.push({
                identifierType: idf.identifierType,
                code: codeObj.code,
                tid: codeObj.tid || ''
              })
            }
          })
        })

      const validatePayload = {
        ...encodingValidation,
        identifiers: [...knownRoleIdentifiers, ...unknownRoleIdentifiers]
      }
      const res = await Encodings.validate(validatePayload, runId)
      if (res) {
        await this.set_identifiers_status(encodingValidation, res, enabledUhfWrite, expectOnlyMandatoryIdentifiers)
        if (checkConfiguration) {
          let configurationDetails
          if (res?.configurationDetails) configurationDetails = res.configurationDetails
          else if (res.configuration?.details) configurationDetails = res.configuration.details
          if ((configurationDetails ?? []).length === 0) {
            this.throwNoConfigurationError()
          }
        }
        return { encodingValidationResponse: res, encodingValidation }
      } else {
        throw new Error(__(T.error.no_item_configuration))
      }
    } catch (error) {
      throw new Error((error as any)?.message ?? __(T.error.no_item_configuration))
    }
  }

  static throwNoConfigurationError(): never {
    throw new Error(__(T.error.no_item_configuration))
  }

  static getMixPanelData(
    operation: EncodingOperationConfig,
    encodingValidation?: EncodingValidationRequest,
    encodingValidationResponse?,
    seconds?,
    force?
  ) {
    const identifiers: any[] = encodingValidation ? this.getIdentifiers(encodingValidation) : []

    const antennas: any = []
    if (AppStore.defaultWorkstation?.antennas && AppStore.defaultWorkstation.antennas.length > 0) {
      AppStore.defaultWorkstation.antennas.forEach((antenna) => {
        antennas.push({
          code: antenna.code,
          txPower: antenna.txPower,
          rxSensitivity: antenna.rxSensitivity,
          reader: {
            code: antenna.reader.code,
            deviceType: antenna.reader.deviceType,
            settings: antenna.reader.settings
          }
        })
      })
    }
    const wsPlace = AppStore.getWorkstationPlace()
    const mixpanelData: any = {
      operationCode: operation.code,
      operationDescription: operation.description,
      identifiers: identifiers,
      antennas: antennas,
      username: AppStore.loggedUser?.username ?? '',
      workstation: AppStore.defaultWorkstation ? AppStore.defaultWorkstation.code : '',
      place: wsPlace?.code ?? ''
    }
    if (seconds) mixpanelData.time = seconds + ' seconds'
    if (encodingValidationResponse) {
      mixpanelData.operationToPerform = encodingValidationResponse.operationToPerform || ''
      mixpanelData.errors = encodingValidationResponse.errors || []
    }
    if (force) mixpanelData.force = force
    identifiers.forEach((idt) => {
      mixpanelData[idt.identifierType] = idt.code
    })
    return mixpanelData
  }

  static getIdentifiers(encodingValidation: EncodingValidationRequest) {
    if (encodingValidation?.identifiers) {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      return identifiers
    } else {
      return []
    }
  }

  static async create(encodingValidation: EncodingValidationRequest, runId?: string) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      const createPayload = { ...encodingValidation, identifiers }
      const res = await Encodings.create(createPayload, runId)
      return res
    } catch {
      throw new Error(__(T.error.item_creation_error))
    }
  }

  static async force_create(encodingValidation: EncodingValidationRequest, pin?: string, runId?: string) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || '',
          desiredCode: idf.desiredCode || '',
          role: idf.role
        }))
        .filter((idf) => !!idf.code)
      const createPayload = { ...encodingValidation, identifiers }
      const res = await Encodings.force_create(createPayload, pin, runId)
      return res
    } catch {
      throw new Error(__(T.error.item_creation_error))
    }
  }

  /**
   * Write passwords for a UHF tag from the tagPasswords endpoint (same call used for the kill).
   * Falls back to the operation's encodingTagPassword when the call fails or returns no write password.
   */
  static async getTagWritePasswords(operation: EncodingOperationConfig, code: string): Promise<string[]> {
    try {
      const res = await Encodings.tagPasswords(operation.id, [{ code, type: 'UHF_TAG' }])
      const writePasswords = res?.[0]?.writePasswords?.filter((p) => !!p) ?? []
      if (writePasswords.length > 0) return writePasswords
    } catch (error) {
      console.warn('tagPasswords call failed, using encodingTagPassword', error)
    }
    return getEncodingTagPassword(operation)
  }

  static async computeTags(
    operation: EncodingOperationConfig,
    identifiers: ConfigurationDetail[],
    workstationId: string,
    startEntityId?: string,
    startEntityCode?: string,
    operationAttributes?: Record<string, unknown>,
    itemAttributes?: Record<string, unknown>,
    runId?: string
  ) {
    return Encodings.computeTags(
      operation.id,
      identifiers,
      workstationId,
      startEntityId,
      startEntityCode,
      operationAttributes,
      itemAttributes,
      runId
    )
  }

  static async onReplaceTagPressedExtension(encodingValidation: EncodingValidationRequest) {
    return true
  }

  static check_tag_is_logic_killable = (data) => {
    return false
  }

  /**
   * Individua l'identifier a cui associare un errore MISSING_TAG.
   * Estratto da verify per permetterne l'override nelle extensions dei custom.
   * @param alreadyAssigned identifier già usati dagli altri MISSING_TAG dello stesso giro di verify
   */
  static findIdentifierForMissingTagError(
    encodingValidation: EncodingValidationRequest,
    error: EncodingError,
    _alreadyAssigned: ConfigurationDetail[]
  ): ConfigurationDetail | undefined {
    return encodingValidation.identifiers.find(
      (idt) =>
        idt.identifierType === error.payload.identifierType &&
        idt.role === error.payload.role &&
        idt._errorCode !== 'Not Associated Tag'
    )
  }

  /**
   * Stabilisce quali identifier di encodingValidation vanno arricchiti con i dati della response di verify.
   * Estratto da verify per permetterne l'override nelle extensions dei custom.
   */
  static shouldEnrichIdentifierFromResponse(idf: ConfigurationDetail) {
    // _read === false: code taken from a previous response, not read (see addMissingConfigurationIdentifiers)
    return !!idf.code && idf._read !== false
  }

  /**
   * Aggiunge a encodingValidation i placeholder per gli identifier obbligatori non ancora presenti.
   * Estratto da verify per permetterne l'override nelle extensions dei custom.
   */
  static addMissingConfigurationIdentifiers(
    encodingValidation: EncodingValidationRequest,
    configurationDetails: ConfigurationDetail[],
    res: EncodingValidationResponse,
    addCodesFromValidationResponse: boolean
  ) {
    configurationDetails
      .filter((c) => !c.optional)
      .forEach((cd) => {
        const findConfiguration = encodingValidation.identifiers.some(
          (f) => f.identifierType === cd.identifierType && f.role === cd.role
        )
        if (!findConfiguration) {
          const identifierToAdd: ConfigurationDetail = {
            identifierType: cd.identifierType,
            role: cd.role ?? '',
            _status: 'TO_BE_READ'
          }
          if (addCodesFromValidationResponse) {
            const findIdentifier = res.item.identifiers.find(
              (idt) => idt.type === cd.identifierType && idt.role === cd.role
            )
            if (findIdentifier) {
              identifierToAdd.code = findIdentifier.code ?? ''
              // code taken from the response, not read: keep it out of the next verify payload
              identifierToAdd._read = false
            }
          }
          encodingValidation.identifiers.push(identifierToAdd)
        }
      })
  }

  static async verify(
    encodingValidation: EncodingValidationRequest,
    addCodesFromValidationResponse = false,
    runId?: string
  ) {
    try {
      const identifiers: ConfigurationDetail[] = encodingValidation.identifiers
        .filter((idf) => idf._read !== false)
        .map((idf) => ({
          identifierType: idf.identifierType,
          code: idf.code,
          tid: idf.tid || ''
        }))
        .filter((idf) => !!idf.code)
      const res = await Encodings.verify({ ...encodingValidation, identifiers }, runId)
      if (res?.item?.identifiers) {
        let configurationDetails: ConfigurationDetail[] = []
        if (res.configuration?.details) configurationDetails = res.configuration.details

        // add data for all encodingValidation.identifiers with code read
        encodingValidation.identifiers
          .filter((idf) => this.shouldEnrichIdentifierFromResponse(idf))
          .forEach((idf) => {
            const find = res.item.identifiers.find(
              (idt) =>
                idt.type === idf.identifierType &&
                (idt.code === idf.code || idt.originalCode === idf.code || (!!idf.tid && idf.tid === idt.tid))
            )
            if (find) {
              idf.identifierId = find.id
              idf.status = find.status ?? ''
              idf.group = find.group ?? ''
              idf.itemId = res.item.id ?? ''
              idf.role = find.role ?? ''
              idf._status = 'PROCESSING'
              idf.productionOrderRowId =
                find.productionOrderRowId && find.productionOrderRowId !== null ? find.productionOrderRowId : undefined
              idf.workOrderRowId = find.workOrderRowId && find.workOrderRowId !== null ? find.workOrderRowId : undefined
              idf.logicKillable = this.check_tag_is_logic_killable(find)
            }
          })

        // check configuration to add identifiers missing
        this.addMissingConfigurationIdentifiers(
          encodingValidation,
          configurationDetails,
          res,
          addCodesFromValidationResponse
        )

        // check tag associated with same group
        encodingValidation.identifiers
          .filter((idf) => !!idf.code)
          .forEach((idf) => {
            const find = res.item.identifiers.find((idt) => idt.code === idf.code && idt.type === idf.identifierType)
            if (find && !!find.group && !!find.code) {
              // find identifier in validate response for same group and differente code and type
              let groups = res.item.identifiers.filter((idt) => idt.group === find.group && idt.type !== find.type)
              if (groups.length === 0) {
                //search identifier with same group and type but different role
                groups = res.item.identifiers.filter(
                  (idt) => idt.group === find.group && idt.type === find.type && idt.role !== find.role
                )
              }

              if (groups.length > 0) {
                groups.forEach((group) => {
                  EncodingExtensions.applyGroupedIdentifier(group, encodingValidation, configurationDetails, res)
                })
              }
            }
          })
      }

      // check and set backend errors
      res.errors.forEach((error) => {
        const errorCode = error.errorCode
        const isNotAssociatedTag = errorCode === 'Not Associated Tag'
        let errorMessage = T.identifier_error[error.errorCode]
          ? __(T.identifier_error[error.errorCode])
          : error.errorCode
        if (errorCode === 'Tag Mismatch') {
          errorMessage = __(T.error.tags_mismatch_error)
          //controllo workorder id
          const uniqueWorkOrderRowIds = [
            ...new Set(
              encodingValidation?.identifiers
                .map((id) => id.workOrderRowId)
                .filter((id): id is string => id !== undefined && id !== null)
            )
          ]
          if (uniqueWorkOrderRowIds.length > 1) errorMessage = __(T.error.tags_mismatch_workorder)
        }
        const index =
          isNotAssociatedTag || (addCodesFromValidationResponse && error.errorCode === 'MISSING_TAG')
            ? -1
            : encodingValidation.identifiers.findIndex((idf) => idf.code === error.ref)
        if (index >= 0) {
          if (error.payload) {
            encodingValidation.identifiers[index].group = error.payload.group ?? ''
            encodingValidation.identifiers[index].role =
              error.payload.role ?? encodingValidation.identifiers[index].role ?? ''
            encodingValidation.identifiers[index].logicKillable = this.check_tag_is_logic_killable(error.payload)
          }
          encodingValidation.identifiers[index]._status = 'ERROR'
          if (encodingValidation.identifiers[index]._errorCode)
            encodingValidation.identifiers[index]._errorCode += ',' + errorCode
          else encodingValidation.identifiers[index]._errorCode = errorCode
          encodingValidation.identifiers[index]._error = errorMessage
        } else if (isNotAssociatedTag) {
          const find = encodingValidation.identifiers.find((idt) => idt.code === error.ref)
          if (error.payload.identifierType !== 'SIMPLE_ITEM_IDENTIFIER') {
            if (find) {
              find.group = error.payload.group ?? ''
              find.role = error.payload.role ?? find.role ?? ''
              find._status = 'ERROR'
              find._error = errorMessage
              find._errorCode = errorCode
              find.logicKillable = this.check_tag_is_logic_killable(error.payload)
            } else {
              encodingValidation.identifiers.push({
                code: error.ref,
                ...error.payload,
                _read: false,
                _status: 'ERROR',
                _error: errorMessage,
                _errorCode: error.errorCode,
                _hidden: true,
                logicKillable: this.check_tag_is_logic_killable(error.payload)
              })
            }
          } else if (
            find &&
            find.status === 'REPLACED_ASSOCIATION' &&
            this.activeReplacedAssocitionForSimpleItemIdentifier()
          ) {
            find._status = 'ERROR'
            find._error = errorMessage
            find._errorCode = errorCode
          }
        }
      })

      //check MISSING_TAG errors
      const missingTagAssigned: ConfigurationDetail[] = []
      res.errors
        .filter((e) => e.errorCode === 'MISSING_TAG')
        .forEach((error) => {
          const errorCode = error.errorCode
          const errorMessage = T.identifier_error[error.errorCode]
            ? __(T.identifier_error[error.errorCode])
            : error.errorCode
          const find = this.findIdentifierForMissingTagError(encodingValidation, error, missingTagAssigned)
          if (find) missingTagAssigned.push(find)
          if (find) {
            // find.code = error.ref
            if (find._status !== 'ERROR') find._status = 'EXPECTED'
            find._error = errorMessage
            find._errorCode = errorCode
            const findIdt = res.item.identifiers.find(
              (idt) => idt.code === find.code && idt.type === find.identifierType
            )
            if (findIdt) {
              find.status = findIdt.status ?? ''
              find.group = findIdt.group ?? ''
              find.itemId = res.item.id ?? ''
              find.role = findIdt.role ?? ''
              find.productionOrderRowId =
                findIdt.productionOrderRowId && findIdt.productionOrderRowId !== null
                  ? findIdt.productionOrderRowId
                  : undefined
              find.workOrderRowId =
                findIdt.workOrderRowId && findIdt.workOrderRowId !== null ? findIdt.workOrderRowId : undefined
              find.logicKillable = this.check_tag_is_logic_killable(findIdt)
            }
          } else if (error.payload.identifierType === 'SIMPLE_ITEM_IDENTIFIER') {
            encodingValidation.identifiers.push({
              code: error.ref,
              ...error.payload,
              _read: false,
              _status: 'EXPECTED',
              _error: errorMessage,
              _errorCode: error.errorCode
            })
          }
        })

      encodingValidation.identifiers
        .filter((idt) => !!idt.code && ['PROCESSING'].includes(idt._status))
        .forEach((idt) => {
          idt._status = 'CONFIRMED'
          idt._error = undefined
        })

      return { encodingValidationResponse: res, encodingValidation }
    } catch (error) {
      throw new Error((error as any).message ?? __(T.error.item_creation_error))
    }
  }

  static getErrorCode = (error) => {
    let errorCode: string = error.errorCode
    if (errorCode === 'Tag Mismatch') errorCode = __(T.error.tags_mismatch_error)
    return errorCode
  }

  /**
   * Returns true when the tag was assigned, false when its NFC mutual-auth failed, undefined when the auth
   * could not run (no antenna, e.g. emulation) and the tag was skipped. Throws NfcAuthSupersededError when a newer
   * NFC authentication superseded this one: callers drop the read.
   */
  static async onTagRead(
    encodingValidation: EncodingValidationRequest,
    _tag: TmrTag,
    operationConfig: EncodingOperationConfig,
    antennaId?: string,
    datadogSession?: DatadogEncodingRun
  ): Promise<boolean | undefined> {
    if (_tag.uid && isNfcAuthEnabled(operationConfig)) {
      if (!antennaId) return undefined
      const success = await readNfcAuthenticatedTag(
        encodingValidation,
        _tag,
        operationConfig,
        antennaId,
        datadogSession
      )
      if (!success) return success
    }

    this.assignTagIdentifier(encodingValidation, _tag)
    return true
  }

  /** Matches an already-authenticated/read tag against the current identifiers and updates or appends it. */
  static assignTagIdentifier(encodingValidation: EncodingValidationRequest, _tag: TmrTag) {
    // define identifier type
    let identifierType = 'UHF_TAG'
    if (_tag.uid) identifierType = 'NFC_TAG'
    if (_tag.barcode) identifierType = 'SIMPLE_ITEM_IDENTIFIER'

    // create tag read
    const tag = {
      code: _tag.epc ?? _tag.uid ?? _tag.barcode,
      tid: _tag.tid || '',
      identifierType
    } as { code: string; tid?: string; identifierType: IdentifierType }

    //search identifiers for regexFilter if exist
    let matchedRolesIdxs: number[] = []
    const identifierWithRegex = encodingValidation.identifiers.filter(
      (idt) => !!idt.regexFilter && idt.regexFilter !== 'sgtin'
    )
    if (identifierWithRegex.length > 0) {
      identifierWithRegex.forEach((idt, i) => {
        const match = new RegExp(idt.regexFilter).exec(tag.code)
        if (match) {
          matchedRolesIdxs.push(i)
        }
      })
    }

    // pick all identifiers with same identifierType and no regexFilter or regexFilter is sgtin
    if (matchedRolesIdxs.length === 0) {
      matchedRolesIdxs = encodingValidation.identifiers.reduce((acc, idt, i) => {
        if ((!idt.regexFilter || idt.regexFilter === 'sgtin') && idt.identifierType === tag.identifierType) {
          acc.push(i)
        }
        return acc
      }, [])
    }

    const tagTid = tag.tid || ''
    const addProcessingIdentifier = () => {
      if (
        encodingValidation.identifiers.some((idt) => idt.code === tag.code && idt.identifierType === tag.identifierType)
      )
        return
      encodingValidation.identifiers.push({
        code: tag.code,
        tid: tagTid,
        identifierType: tag.identifierType,
        _status: 'PROCESSING',
        _added: true
      })
    }
    const setProcessingFlags = (identifier: ConfigurationDetail) => {
      identifier._status = 'PROCESSING'
      identifier._error = undefined
      // identifier._read = true
      identifier._hidden = false
    }
    const pushPossibleCode = (identifier: ConfigurationDetail) => {
      if (!identifier._possibleCodes) {
        identifier._possibleCodes = []
      }
      if (identifier._possibleCodes.some((pc) => pc.code === tag.code)) return
      identifier._possibleCodes.push({ code: tag.code, tid: tagTid })
    }

    const processMatchedRoleWithCode = (identifier: ConfigurationDetail, onlyOneMatchingRoleIsFound: boolean) => {
      if (identifier.role) {
        const codeIsEqual = identifier.code === tag.code
        if (codeIsEqual) {
          identifier._read = true
          setProcessingFlags(identifier)
          if (!onlyOneMatchingRoleIsFound) {
            pushPossibleCode(identifier)
          }
        } else {
          addProcessingIdentifier()
        }
      } else {
        // the tag with the right role has been already read, so we add a new one
        addProcessingIdentifier()
      }
    }

    if (matchedRolesIdxs.length > 0) {
      const onlyOneMatchingRoleIsFound = matchedRolesIdxs.length === 1
      // in case of not-univocal match with the role, the role attribution is demanded to the backend
      // and the code is set later
      matchedRolesIdxs.forEach((index) => {
        const identifier = encodingValidation.identifiers[index]
        if (!onlyOneMatchingRoleIsFound && ['CONFIRMED', 'ERROR'].includes(identifier._status)) return
        if (identifier.code) {
          processMatchedRoleWithCode(identifier, onlyOneMatchingRoleIsFound)
          return
        }

        // just update the identifier box
        identifier.code = onlyOneMatchingRoleIsFound ? tag.code : undefined
        identifier.tid = onlyOneMatchingRoleIsFound ? tagTid : undefined
        identifier._read = onlyOneMatchingRoleIsFound
        setProcessingFlags(identifier)
        if (!onlyOneMatchingRoleIsFound && !encodingValidation.identifiers.some((idf) => idf.code === tag.code)) {
          pushPossibleCode(identifier)
        }
      })
    } else {
      addProcessingIdentifier()
    }
  }

  /** Custom-commands operation type for the antenna, when the operation uses mutualAuth2Step and only one NFC antenna is reading. */
  static getAntennaCustomCommandsOperationType(operationConfig: { options?: NfcAuthOperationOptions }) {
    if (!isMutualAuth2StepEnabled(operationConfig)) return undefined
    const nfcAntennas = RfidReader.getAntennas().filter((a) => a.reader?.settings?.supportedTagType === 'NFC')
    return nfcAntennas.length > 1 ? undefined : CUSTOM_COMMANDS_OPERATION_TYPE
  }

  static resetIdentifiersEncodingValition(
    operation: EncodingOperationConfig,
    encodingValidationResponse?: EncodingValidationResponse,
    encodingValidation?: EncodingValidationRequest
  ) {
    const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(operation)
    const identifiers: ConfigurationDetail[] = []
    if (encodingValidationResponse?.configuration) {
      let configurationDetails
      if (encodingValidationResponse.configurationDetails)
        configurationDetails = encodingValidationResponse.configurationDetails
      else if (encodingValidationResponse.configuration?.details)
        configurationDetails = encodingValidationResponse.configuration.details
      if (!configurationDetails) configurationDetails = []

      configurationDetails.forEach((configDetail) => {
        if (!expectOnlyMandatoryIdentifiers || (expectOnlyMandatoryIdentifiers && !configDetail.optional)) {
          identifiers.push({ ...configDetail })
        }
      })
    }
    return identifiers
  }

  static getOptionEncodingPage(value, encodingConfig: EncodingOperationConfig) {
    if (encodingConfig.options.showOnlyVerify === 'true') {
      Router.navigate(`/encoding/:opCode/verify`, { opCode: encodingConfig.code })
      return
    }
    switch (value) {
      case 'associate':
        Router.navigate(`/encoding/:opCode`, { opCode: encodingConfig.code })
        break
      case 'verify':
        Router.navigate(`/encoding/:opCode/verify`, { opCode: encodingConfig.code })
        break
      default:
        Router.navigate(`/encoding/:opCode`, { opCode: encodingConfig.code })
        break
    }
  }

  static sortIdentifiers(identifiers: ConfigurationDetail[]) {
    //Sort identifiers with first the identifierType as SIMPLEITEMIDENTIFIER and then the others
    return identifiers.sort((a, b) => {
      if (a.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && a._status !== 'CONFIRMED') {
        return -1
      } else if (b.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && b._status !== 'CONFIRMED') {
        return 1
      }
      return 0
    })
  }

  static getEncodingProduct = (encodingValidationResponse: EncodingValidationResponse | undefined) => {
    let product
    if (encodingValidationResponse?.item) {
      product = encodingValidationResponse.item.product
      if (enabledWamGroupedProduct() && encodingValidationResponse.item.attributes?.wam) {
        if (!product.attributes) {
          product.attributes = {}
        }
        product.attributes.wam = encodingValidationResponse.item.attributes.wam
      }
    }
    return product
  }

  static getProductionOrderFields = (_operation?: EncodingOperationConfig) => [
    { label: __(T.misc.order_type), path: 'orderType' }
  ]

  static getOverrideProductInfoFields = (
    _context: OverrideProductInfoFieldsContext,
    _operation: EncodingOperationConfig
  ) => null

  static getOrderedProductionOrderRows = (productionOrderRows: ProductionOrderRowDetail[]) => {
    return [...productionOrderRows].sort((a, b) =>
      compareSizeLabels(a.product?.size?.value ?? '', b.product?.size?.value ?? '')
    )
  }

  static getProductionOrderDetails = ({
    productionOrderRow
  }: GetProductionOrderDetails): {
    subtitle: string
    data: ProductionOrder | ProductionOrderRow | undefined
  } => {
    return { subtitle: productionOrderRow?.productionOrder?.code, data: productionOrderRow?.productionOrder }
  }

  static getWorkOrderDetails = ({
    workOrderRow
  }: GetProductionOrderDetails): {
    subtitle: string
    data: WorkOrder | undefined
  } => {
    return { subtitle: workOrderRow?.workOrder?.code, data: workOrderRow?.workOrder }
  }
}
